<script lang="ts">
  import clsx from 'clsx';
  import { Card } from '@athena-lib/Card';
  import { BaseHeading, Paragraph } from '@athena-lib/Text';
  import { getColorContext, initializeColorContext } from '@athena-lib/container-context.svelte';
  import { Stack, StackItem } from '@athena-lib/Stack';
  import { parseHeadingLevel } from '@athena-lib/Text/BaseHeading/types';
  import type { ImageCardProps } from '../types';

  const {
    label,
    title,
    titleHeadingTag = '3',
    description,
    class: className,
    textColor = 'light',
    labelColor,
    ...props
  }: ImageCardProps = $props();
  const currentContext = getColorContext();
  initializeColorContext({
    ...currentContext,
    lg: 'dark',
    md: 'dark',
    sm: 'dark',
  });

  // The image card height should be a ratio of 4/5 to its width when three cards are displayed in a row
  // with 2rem spacing between them. We can't hard code this into a pixel height because the height becomes
  // smaller in smaller screens. So we need to calculate it based on the width of the screen/max width of a page.
  // The expression below means:
  // (min(1230px,calc(100vw-4rem)): The min value of either the full page width of 1230px or the width of the screen
  // minus 4rem where 4rem is the padding of the page.
  // The -4rem next is to remove the spacing between the three cards (2rem on each side).
  // /3: Divide the width by 3 to get the width of one card.
  // * 1.25: Multiply the width by 1.25 to get the height of the card when the aspect ratio is 4/5.
  // * 1.234: Slightly less than 1.25, used to better fit the card height within the available space at the 'md' breakpoint,
  const heightClass =
    'h-[calc(calc(100vw-4rem)*1.25)] md:h-[calc(((min(1230px,calc(100vw-4rem))-4rem)/3)*1.234)]';
</script>

<Card
  {...props}
  aspect="4/5"
  class={clsx(heightClass, className)}
  padding="medium"
  useMinHeight={false}
  gradientColor={!textColor || textColor === 'light' ? 'dark' : 'light'}
>
  <Stack space="small" class="pointer-events-auto">
    <StackItem>
      {#if label}
        <BaseHeading
          level={parseHeadingLevel(titleHeadingTag) ?? 3}
          color={(labelColor ?? labelColor === 'dark') ? 'magenta' : 'brand'}
          class={clsx('text-st-sm-xsmall font-bold uppercase lg:text-st-lg-xsmall')}
          size={{ sm: 'small', md: 'medium', lg: 'medium' }}
          >{label}
        </BaseHeading>
      {/if}
      {#if title}
        <BaseHeading
          level={parseHeadingLevel(titleHeadingTag) ?? 3}
          element="div"
          color={textColor}
          size={{ sm: 'small', md: 'medium', lg: 'medium' }}
        >
          {title}
        </BaseHeading>
      {/if}
    </StackItem>
    {#if description}
      <StackItem>
        <Paragraph
          color={textColor}
          size="large"
          class={clsx('line-clamp-2 sm:line-clamp-3 lg:line-clamp-none')}>{description}</Paragraph
        >
      </StackItem>
    {/if}
  </Stack>
</Card>
